/**
 * ChatGPT-style audio-reactive particle sphere.
 *
 * ~150 small particles distributed in a unit sphere, projected to 2D each
 * frame via a simple orthographic + perspective scale. Per-state palettes
 * crossfade over 250ms; particle motion modulates with audio levels.
 *
 *   const ps = new ParticleSphere(canvas);
 *   ps.start();
 *   ps.setState('listening');
 *   ps.setLevels(0.3, 0.0);  // input, output ∈ [0,1]
 *   ps.stop();
 *
 * Auto-pauses on `visibilitychange:hidden`. Honors `prefers-reduced-motion`
 * by rendering a static gradient sphere instead of an animated particle field.
 *
 * No external deps; ~6 KB raw / ~2.5 KB gzipped after Terser.
 */

import type { ConvState } from '../conversation';

/**
 * Particle count is responsive to canvas size — keeps visual density
 * consistent and avoids burning frames on mobile/small embeds.
 *
 * Rough sweet-spots (CSS-pixel min-dimension → particle count):
 *   240px → ~140    (tight mobile/sidebar)
 *   340px → ~250    (compact embed)
 *   480px → ~350    (default desktop)
 *   640px+→  380    (cap; diminishing returns past this)
 */
function particleCountFor(minSidePx: number): number {
  const n = Math.round(140 + (minSidePx - 240) * (380 - 140) / (640 - 240));
  if (n < 100) return 100;
  if (n > 380) return 380;
  return n;
}
const LEVEL_SMOOTHING = 0.18;       // exponential ease toward target audio level per frame
const STATE_TRANSITION_MS = 280;
const TWO_PI = Math.PI * 2;
/** Sphere size as a fraction of the smaller canvas dimension. */
const SPHERE_RADIUS_FRAC = 0.34;

type RGB = readonly [number, number, number];
interface Palette {
  /** Two anchor colors; particles' hue mixed by per-particle `hueT`. */
  readonly a: RGB;
  readonly b: RGB;
  /** Halo intensity multiplier (0..1.5). */
  readonly halo: number;
}

// Tech-aesthetic palettes: white / neon-blue at rest, neon-green when Nova
// speaks. Anchors are saturated; particles render as crisp pinpoints, bloom
// pulls the color out into ambient light.
const PALETTES: Record<ConvState, Palette> = {
  idle:       { a: [240, 250, 255], b: [ 56, 189, 248], halo: 0.6  }, // cool white → neon blue
  connecting: { a: [255, 255, 255], b: [125, 211, 252], halo: 0.8  }, // brighter — pure white edge
  listening:  { a: [ 56, 189, 248], b: [165, 243, 252], halo: 0.95 }, // neon blue → bright cyan
  thinking:   { a: [255, 255, 255], b: [186, 230, 253], halo: 1.05 }, // bright white shimmer
  speaking:   { a: [ 74, 222, 128], b: [167, 243, 208], halo: 1.2  }, // neon green → mint
  error:      { a: [248, 113, 113], b: [220,  38,  38], halo: 0.5  }, // red (unchanged)
};

interface Particle {
  // unit-sphere position (will roughly stay near r ≈ targetRadius)
  x: number; y: number; z: number;
  // velocity
  vx: number; vy: number; vz: number;
  // size jitter (px before DPR scale)
  size: number;
  // hue mixing parameter ∈ [0,1] (fixed)
  hueT: number;
}

export class ParticleSphere {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;

  private particles: Particle[] = [];
  private rafId: number | null = null;
  private startedAt = 0;

  private state: ConvState = 'idle';
  private prevState: ConvState = 'idle';
  private stateChangeAt = 0;

  private smoothedInput = 0;
  private smoothedOutput = 0;
  private targetInput = 0;
  private targetOutput = 0;

  private dpr = 1;
  private widthPx = 0;
  private heightPx = 0;

  private prefersReducedMotion = false;

  private readonly resizeObserver: ResizeObserver;
  private readonly cleanupFns: Array<() => void> = [];

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) throw new Error('ParticleSphere: 2d canvas context unavailable');
    this.ctx = ctx;

    this.spawnParticles();
    this.handleResize();

    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(canvas);

    const onVisibility = (): void => {
      if (document.hidden) this.pauseLoop();
      else if (this.startedAt > 0) this.resumeLoop();
    };
    document.addEventListener('visibilitychange', onVisibility);
    this.cleanupFns.push(() => document.removeEventListener('visibilitychange', onVisibility));

    const motionMQ = matchMedia('(prefers-reduced-motion: reduce)');
    this.prefersReducedMotion = motionMQ.matches;
    const onMotionChange = (e: MediaQueryListEvent): void => {
      this.prefersReducedMotion = e.matches;
    };
    motionMQ.addEventListener('change', onMotionChange);
    this.cleanupFns.push(() => motionMQ.removeEventListener('change', onMotionChange));
  }

  /** Begin the render loop. Idempotent. */
  start(): void {
    if (this.rafId !== null) return;
    this.startedAt = performance.now();
    this.stateChangeAt = this.startedAt;
    this.tick();
  }

  /** Halt the render loop and disconnect observers. After stop(), the instance
   *  is dead — make a new one if you need it again. */
  stop(): void {
    this.pauseLoop();
    this.resizeObserver.disconnect();
    for (const fn of this.cleanupFns) fn();
    this.cleanupFns.length = 0;
    this.startedAt = 0;
  }

  setState(state: ConvState): void {
    if (state === this.state) return;
    this.prevState = this.state;
    this.state = state;
    this.stateChangeAt = performance.now();
  }

  /** input/output ∈ [0,1] from the SDK's level event. Smoothed internally. */
  setLevels(input: number, output: number): void {
    this.targetInput = clamp01(input);
    this.targetOutput = clamp01(output);
  }

  // ---------------------------------------------------------------- internals

  private spawnParticles(): void {
    this.particles = [];
    // Use canvas size at spawn time. Re-spawning isn't strictly needed on
    // resize since the projection scales with canvas size, but if the canvas
    // grows substantially we'd be under-dense — fine for v1.
    const rect = this.canvas.getBoundingClientRect();
    const minSide = Math.min(rect.width || 320, rect.height || 320);
    const count = particleCountFor(minSide);
    for (let i = 0; i < count; i++) {
      // uniform sphere via Marsaglia: reject points outside the unit ball
      let x: number, y: number, z: number, d2: number;
      do {
        x = Math.random() * 2 - 1;
        y = Math.random() * 2 - 1;
        z = Math.random() * 2 - 1;
        d2 = x * x + y * y + z * z;
      } while (d2 > 1 || d2 < 0.001);
      // normalize to surface, then offset slightly inward so we have density
      const r = 0.7 + Math.random() * 0.15;
      const inv = r / Math.sqrt(d2);
      this.particles.push({
        x: x * inv,
        y: y * inv,
        z: z * inv,
        vx: 0,
        vy: 0,
        vz: 0,
        // Smaller pinpoints — most are 0.8–1.4 px, with a sprinkling of 1.4–1.8
        // for visual variety. Crisp on retina; bloom pass adds the glow.
        size: 0.8 + Math.random() * (Math.random() < 0.15 ? 1.0 : 0.6),
        hueT: Math.random(),
      });
    }
  }

  private handleResize(): void {
    const rect = this.canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.max(1, Math.round(rect.width * this.dpr));
    const h = Math.max(1, Math.round(rect.height * this.dpr));
    if (w !== this.widthPx || h !== this.heightPx) {
      this.widthPx = w;
      this.heightPx = h;
      this.canvas.width = w;
      this.canvas.height = h;
    }
  }

  private pauseLoop(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private resumeLoop(): void {
    if (this.rafId === null && !document.hidden) this.tick();
  }

  private readonly tick = (): void => {
    if (document.hidden) {
      this.rafId = null;
      return;
    }
    const now = performance.now();
    const tSec = (now - this.startedAt) / 1000;

    // ease audio levels toward target
    this.smoothedInput += (this.targetInput - this.smoothedInput) * LEVEL_SMOOTHING;
    this.smoothedOutput += (this.targetOutput - this.smoothedOutput) * LEVEL_SMOOTHING;

    if (!this.prefersReducedMotion) this.updateParticles(tSec);
    this.draw(tSec);

    this.rafId = requestAnimationFrame(this.tick);
  };

  private updateParticles(tSec: number): void {
    const state = this.state;

    // breath: slow oscillator ∈ [0,1]. Thinking gets its own faster cycle so
    // visitors see "something happening" between user-finished and Nova-speaks.
    const breathRate =
      state === 'connecting' ? 1.2 :
      state === 'thinking'   ? 0.9 :
      3.5;
    const breath = 0.5 + 0.5 * Math.sin((tSec * TWO_PI) / breathRate);

    // jitter strength + target radius differ per state
    let jitter = 0.0018;
    let targetR = 0.78 + 0.06 * breath;
    if (state === 'listening') {
      jitter = 0.0028 + this.smoothedInput * 0.012;
      targetR = 0.72 + 0.05 * breath - this.smoothedInput * 0.16;
    } else if (state === 'thinking') {
      // High activity, no audio coupling — pure "processing" feel.
      // Particles shimmer in tight orbit; faster breath signals "working."
      jitter = 0.005;
      targetR = 0.7 + 0.04 * breath + 0.02 * Math.sin(tSec * 6);
    } else if (state === 'speaking') {
      jitter = 0.004 + this.smoothedOutput * 0.018;
      targetR = 0.82 + 0.06 * breath + this.smoothedOutput * 0.22;
    } else if (state === 'connecting') {
      jitter = 0.003;
      targetR = 0.7 + 0.04 * breath;
    } else if (state === 'error') {
      jitter = 0.0008;
      targetR = 0.9;
    }

    for (const p of this.particles) {
      // Brownian-ish jitter
      p.vx += (Math.random() - 0.5) * jitter;
      p.vy += (Math.random() - 0.5) * jitter;
      p.vz += (Math.random() - 0.5) * jitter;
      // integrate
      p.x += p.vx;
      p.y += p.vy;
      p.z += p.vz;
      // damping (higher in error state to settle motion)
      const damp = state === 'error' ? 0.86 : 0.92;
      p.vx *= damp;
      p.vy *= damp;
      p.vz *= damp;
      // soft spring toward sphere of radius targetR
      const dist = Math.sqrt(p.x * p.x + p.y * p.y + p.z * p.z) || 0.0001;
      const pull = (targetR - dist) * 0.05;
      p.x += (p.x / dist) * pull;
      p.y += (p.y / dist) * pull;
      p.z += (p.z / dist) * pull;
    }
  }

  private draw(tSec: number): void {
    const ctx = this.ctx;
    const w = this.widthPx;
    const h = this.heightPx;
    const cx = w / 2;
    const cy = h / 2;
    const baseRadius = Math.min(w, h) * SPHERE_RADIUS_FRAC;
    const palette = this.currentPalette();

    // Clear with transparent — host's background shows through (consistent
    // with a Shadow-DOM-isolated component in any host page).
    ctx.clearRect(0, 0, w, h);

    // Soft halo behind the sphere
    ctx.globalCompositeOperation = 'lighter';
    const haloR = baseRadius * 1.7;
    const halo = ctx.createRadialGradient(cx, cy, 0, cx, cy, haloR);
    halo.addColorStop(0, rgba(palette.a, 0.16 * palette.halo));
    halo.addColorStop(0.55, rgba(palette.a, 0.06 * palette.halo));
    halo.addColorStop(1, rgba(palette.a, 0));
    ctx.fillStyle = halo;
    ctx.fillRect(0, 0, w, h);

    if (this.prefersReducedMotion) {
      this.drawStaticSphere(cx, cy, baseRadius, palette, tSec);
    } else {
      this.drawParticles(cx, cy, baseRadius, palette);
    }

    ctx.globalCompositeOperation = 'source-over';
  }

  private drawParticles(cx: number, cy: number, baseRadius: number, palette: Palette): void {
    const ctx = this.ctx;
    const dpr = this.dpr;

    // Two-pass rendering for crisp pinpoints with cheap bloom:
    //   1. Bloom pass — larger, low-alpha disks (no per-particle gradients)
    //      give an ambient glow that varies by depth and palette.
    //   2. Core pass  — small, near-opaque disks for the sharp pinpoint.
    //
    // Pre-computing per-particle pixel coords once + reusing in both passes
    // is faster than running the geometry math twice.

    type Cached = { px: number; py: number; r: number; g: number; b: number; alpha: number; size: number };
    const cache: Cached[] = new Array(this.particles.length);
    for (let i = 0; i < this.particles.length; i++) {
      const p = this.particles[i]!;
      const persp = 1 + p.z * 0.42;
      const px = cx + p.x * baseRadius * persp;
      const py = cy + p.y * baseRadius * persp;
      const depthT = (p.z + 1) * 0.5;                // 0..1
      const alpha = 0.45 + 0.55 * depthT;            // 0.45..1.0
      const sz = p.size * persp * dpr;
      const r = lerp(palette.a[0], palette.b[0], p.hueT) | 0;
      const g = lerp(palette.a[1], palette.b[1], p.hueT) | 0;
      const b = lerp(palette.a[2], palette.b[2], p.hueT) | 0;
      cache[i] = { px, py, r, g, b, alpha, size: sz };
    }

    // Bloom pass — additive blending makes overlapping bloom disks brighten.
    ctx.globalCompositeOperation = 'lighter';
    for (let i = 0; i < cache.length; i++) {
      const c = cache[i]!;
      ctx.fillStyle = `rgba(${c.r},${c.g},${c.b},${(c.alpha * 0.18).toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(c.px, c.py, c.size * 3.0, 0, TWO_PI);
      ctx.fill();
    }

    // Core pass — sharp pinpoints. Switch back to source-over so cores
    // don't get washed out by additive accumulation.
    ctx.globalCompositeOperation = 'source-over';
    for (let i = 0; i < cache.length; i++) {
      const c = cache[i]!;
      ctx.fillStyle = `rgba(${c.r},${c.g},${c.b},${c.alpha.toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(c.px, c.py, c.size, 0, TWO_PI);
      ctx.fill();
    }
  }

  private drawStaticSphere(cx: number, cy: number, baseRadius: number, palette: Palette, tSec: number): void {
    // Single-stroke fallback for users who'd rather not see motion.
    const ctx = this.ctx;
    // very slow hue cycle so it doesn't feel dead
    const phase = (tSec * 0.05) % 1;
    const t = phase < 0.5 ? phase * 2 : (1 - phase) * 2;
    const ar = lerp(palette.a[0], palette.b[0], t) | 0;
    const ag = lerp(palette.a[1], palette.b[1], t) | 0;
    const ab = lerp(palette.a[2], palette.b[2], t) | 0;
    const grad = ctx.createRadialGradient(
      cx - baseRadius * 0.3,
      cy - baseRadius * 0.3,
      0,
      cx,
      cy,
      baseRadius,
    );
    grad.addColorStop(0, `rgba(${ar},${ag},${ab},0.95)`);
    grad.addColorStop(0.7, `rgba(${ar},${ag},${ab},0.35)`);
    grad.addColorStop(1, `rgba(${ar},${ag},${ab},0)`);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cy, baseRadius, 0, TWO_PI);
    ctx.fill();
  }

  private currentPalette(): Palette {
    const elapsed = performance.now() - this.stateChangeAt;
    const t = clamp01(elapsed / STATE_TRANSITION_MS);
    if (t >= 1 || this.prevState === this.state) return PALETTES[this.state];
    const from = PALETTES[this.prevState];
    const to = PALETTES[this.state];
    return {
      a: [
        lerp(from.a[0], to.a[0], t),
        lerp(from.a[1], to.a[1], t),
        lerp(from.a[2], to.a[2], t),
      ] as const,
      b: [
        lerp(from.b[0], to.b[0], t),
        lerp(from.b[1], to.b[1], t),
        lerp(from.b[2], to.b[2], t),
      ] as const,
      halo: lerp(from.halo, to.halo, t),
    };
  }
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function rgba(c: RGB, alpha: number): string {
  return `rgba(${c[0] | 0},${c[1] | 0},${c[2] | 0},${alpha.toFixed(3)})`;
}
